/**
 * Deep-merge plain objects.
 *
 * Rules (type-aware, to protect code defaults against corrupted overrides):
 * - `null` / `undefined` source keeps the target (code default).
 * - Primitive source (string/number/boolean, incl. "") replaces target.
 * - Array source replaces an array target (list replacement), but is IGNORED
 *   when the target is a plain object (type conflict = corrupted override).
 * - Plain-object source merges into a plain-object target, but is IGNORED when
 *   the target is an array (type conflict = corrupted override).
 */
export function deepMerge(target, source) {
  if (source === null || source === undefined) {
    return target;
  }
  if (typeof source !== "object") {
    return source;
  }

  const sourceIsArray = Array.isArray(source);
  const targetIsObject = target !== null && typeof target === "object";
  const targetIsArray = Array.isArray(target);

  if (sourceIsArray) {
    // An array override is only valid over an array default (or when the code
    // has no object default to protect). If the code default is a plain object,
    // a stray array is corrupted data and must not wipe it.
    if (targetIsObject && !targetIsArray) {
      return target;
    }
    return source;
  }

  // source is a plain object
  if (targetIsArray) {
    // Code default is an array but the override is an object → corrupted; keep code.
    return target;
  }

  const base = targetIsObject && !targetIsArray ? { ...target } : {};
  for (const key of Object.keys(source)) {
    base[key] = deepMerge(base[key], source[key]);
  }
  return base;
}
